import type { Bundle, ResourceType } from '@medplum/fhirtypes'
import {
  ExtendedResource,
  type ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import {
  fhirSearchUrl,
  matchesSearchRequest,
  parseSearchRequest,
  tryGetSchema,
} from '@seen/core/medplum'
import { normalizeQuery, type QueryTypes } from '@seen/core/search'
import type { Query, QueryClient } from '@tanstack/react-query'
import {
  castArray,
  compact,
  groupBy,
  isEqual,
  throttle,
  uniqBy,
} from 'lodash-es'
import { useCallback, useEffect, useRef } from 'react'
import { NO_QUERY } from '#src/constants/no-query.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplumOptional } from '#src/hooks/useMedplum.ts'
import {
  setWebSocketConnectionStatus,
  setWebSocketTabVisibility,
  WEB_SOCKET_DISCONNECT_DEBOUNCE_MS,
  WEB_SOCKET_RECONNECT_DEBOUNCE_MS,
} from '#src/hooks/useWebSocketConnectionStatus.ts'

/* Bulk backfills/migrations emit one subscription message per touched
 * resource, each of which can trigger a refetch for every matching query.
 * Coalesce refetches per queryKey within this window so a migration
 * touching thousands of resources of one type doesn't fire thousands of
 * network requests. */
const QUERY_REFETCH_THROTTLE_MS = 3000

interface BaseMeta extends Record<string, unknown> {
  queryParams: QueryTypes
  resourceType?: ResourceType
  resourceClass?: ExtendedResourceConstructor<any>
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: BaseMeta
    mutationMeta: BaseMeta
  }
}

export function useSyncCache(queryClient: QueryClient): void {
  const medplum = useMedplumOptional()
  const requestedSchemas = useRef(new Set<ResourceType>())
  /* Tracks which resource types have active Medplum subscriptions. Shared
   * between the cache-subscription effect (adds/removes per query lifecycle)
   * and the visibility-management effect (bulk unsubscribe on background,
   * re-sync on foreground). Both effects run synchronously in the browser
   * event loop so concurrent mutation is not a concern. */
  const subscribedCriteria = useRef(new Set<ResourceType>())
  const isTabVisible = useRef<boolean>(true)
  const disconnectTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )
  /* queryKey (stringified) -> throttled fetch for that query. Lets bursts of
   * subscription messages targeting the same query collapse: the first
   * message refetches immediately (leading edge), later messages within the
   * window are dropped, and one final refetch fires after the window
   * (trailing edge) to pick up the burst's last state. The throttled
   * function reads the latest `Query` instance from `latestQueries` at call
   * time rather than closing over it, so a query object recreated by React
   * Query's GC mid-window is still the one that gets fetched. */
  const throttledFetches = useRef(
    new Map<string, ReturnType<typeof throttle<() => void>>>()
  )
  const latestQueries = useRef(new Map<string, Query>())

  const authenticated = medplum?.isAuthenticated()

  const throttledFetch = useCallback((query: Query): void => {
    const key = query.queryHash
    latestQueries.current.set(key, query)
    let fetch = throttledFetches.current.get(key)
    if (!fetch) {
      fetch = throttle(
        () => {
          const latest = latestQueries.current.get(key)
          if (latest) {
            void latest.fetch(latest.options)
          }
        },
        QUERY_REFETCH_THROTTLE_MS,
        { leading: true, trailing: true }
      )
      throttledFetches.current.set(key, fetch)
    }
    fetch()
  }, [])

  const subscribeToCriteria = useCallback(
    (resourceType: ResourceType): void => {
      if (!medplum || !isTabVisible.current || !medplum.isAuthenticated()) {
        return
      }
      if (subscribedCriteria.current.has(resourceType)) {
        return
      }
      medplum.subscribeToCriteria(resourceType)
      subscribedCriteria.current.add(resourceType)
    },
    [medplum]
  )
  const unsubscribeFromCriteria = useCallback(
    (resourceType: ResourceType): void => {
      if (!medplum) {
        return
      }
      medplum.unsubscribeFromCriteria(resourceType)
      subscribedCriteria.current.delete(resourceType)
    },
    [medplum]
  )
  const unsubscribeFromAllCriteria = useCallback((): void => {
    if (!medplum) {
      return
    }
    medplum.getSubscriptionManager().closeWebSocket()
    subscribedCriteria.current.clear()
  }, [medplum])

  useEffect(() => {
    if (!authenticated) {
      unsubscribeFromAllCriteria()
    }
  }, [authenticated, unsubscribeFromAllCriteria])

  useEffect(() => {
    if (!medplum) {
      return
    }

    const cache = queryClient.getQueryCache()

    return cache.subscribe((event) => {
      if (
        !(
          event.type === 'added' ||
          event.type === 'updated' ||
          event.type === 'removed'
        )
      ) {
        return
      }

      const { resourceType: metaResourceType, resourceClass } =
        event.query.meta ?? {}

      const resourceType = metaResourceType ?? resourceClass?.RESOURCE_TYPE
      if (!resourceType) {
        return
      }

      if (!requestedSchemas.current.has(resourceType)) {
        requestedSchemas.current.add(resourceType)
        void medplum.requestSchema(resourceType)
      }

      if (event.type === 'removed') {
        const hasAnyRemainingQueriesForResourceType =
          cache.findAll({
            queryKey: [resourceType],
          }).length > 0
        if (!hasAnyRemainingQueriesForResourceType) {
          unsubscribeFromCriteria(resourceType)
        }
        return
      }

      subscribeToCriteria(resourceType)
    })
  }, [queryClient, medplum, subscribeToCriteria, unsubscribeFromCriteria])

  useEffect(() => {
    if (!medplum) {
      return
    }

    const cache = queryClient.getQueryCache()

    const clearDisconnectTimeout = (): void => {
      if (!disconnectTimeout.current) {
        return
      }
      clearTimeout(disconnectTimeout.current)
      disconnectTimeout.current = undefined
    }

    const clearReconnectTimeout = (): void => {
      if (!reconnectTimeout.current) {
        return
      }
      clearTimeout(reconnectTimeout.current)
      reconnectTimeout.current = undefined
    }

    const syncCriteriaWithCache = (): void => {
      if (!isTabVisible.current) {
        return
      }

      const queryResources = new Set<ResourceType>()
      cache
        .findAll()
        .map((query) => {
          const { resourceType: metaResourceType, resourceClass } =
            query.meta ?? {}
          return metaResourceType ?? resourceClass?.RESOURCE_TYPE
        })
        .filter((resourceType): resourceType is ResourceType => !!resourceType)
        .forEach((resourceType) => {
          queryResources.add(resourceType)
        })

      queryResources.forEach((resourceType) => {
        subscribeToCriteria(resourceType)
      })
    }

    const handleTabBackgrounded = (): void => {
      clearReconnectTimeout()
      clearDisconnectTimeout()
      disconnectTimeout.current = setTimeout(() => {
        if (isTabVisible.current) {
          return
        }

        unsubscribeFromAllCriteria()
        setWebSocketConnectionStatus('paused')
      }, WEB_SOCKET_DISCONNECT_DEBOUNCE_MS)
    }

    const handleTabFocused = (): void => {
      clearDisconnectTimeout()
      clearReconnectTimeout()
      reconnectTimeout.current = setTimeout(() => {
        if (!isTabVisible.current) {
          return
        }

        syncCriteriaWithCache()
        /* Check the actual WebSocket transport state rather than assuming
         * it's still open. If it dropped while backgrounded, set 'connecting'
         * and let connectHandler confirm when it's back. */
        const isWsOpen =
          medplum.getSubscriptionManager().getWebSocket().readyState ===
          WebSocket.OPEN
        setWebSocketConnectionStatus(isWsOpen ? 'connected' : 'connecting')
      }, WEB_SOCKET_RECONNECT_DEBOUNCE_MS)
    }

    /*
     * Only listen to visibilitychange, not window focus/blur.
     * focus/blur fire on OS-level window switches (Alt-Tab) where
     * document.hidden stays false, causing spurious reconnect cycles.
     * visibilitychange correctly captures tab switching in modern browsers.
     *
     * Data freshness after resubscribing relies on TanStack Query's default
     * refetchOnWindowFocus: true to refetch stale queries when the tab regains focus.
     */
    const handleVisibilityChange = (): void => {
      const nextIsVisible = !document.hidden
      isTabVisible.current = nextIsVisible
      setWebSocketTabVisibility(nextIsVisible)

      if (nextIsVisible) {
        handleTabFocused()
        return
      }

      handleTabBackgrounded()
    }

    handleVisibilityChange()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearDisconnectTimeout()
      clearReconnectTimeout()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    queryClient,
    medplum,
    subscribeToCriteria,
    unsubscribeFromCriteria,
    unsubscribeFromAllCriteria,
  ])

  useEffect(() => {
    if (!medplum) {
      return
    }

    const cache = queryClient.getQueryCache()

    const subscriptionEmitter = medplum.getMasterSubscriptionEmitter()
    const throttledFetchesAtMount = throttledFetches.current
    const latestQueriesAtMount = latestQueries.current

    /* Medplum v4.5.2 bug: status.type throws when entry[0].resource is undefined.
     * https://github.com/medplum/medplum/blob/v4.5.2/packages/core/src/subscriptions/index.ts */
    const errorHandler = (event: { type: 'error'; payload: Error }): void => {
      console.error('[useSyncCache] Subscription error:', {
        error: event.payload,
        message: event.payload?.message,
        stack: event.payload?.stack,
      })
    }

    const connectHandler = (_event: {
      type: 'connect'
      payload: { subscriptionId: string }
    }): void => {
      setWebSocketConnectionStatus(
        isTabVisible.current ? 'connected' : 'paused'
      )
    }

    const disconnectHandler = (event: {
      type: 'disconnect'
      payload: { subscriptionId: string }
    }): void => {
      setWebSocketConnectionStatus(
        isTabVisible.current ? 'disconnected' : 'paused'
      )
      console.warn('[useSyncCache] Subscription disconnected:', event.payload)
    }

    const messageHandler = (event: {
      type: 'message'
      payload: Bundle
    }): void => {
      if (!event?.payload) {
        console.error('[useSyncCache] Received malformed event:', {
          hasEvent: !!event,
          eventType: event?.type,
          hasPayload: !!event?.payload,
          payloadType: typeof event?.payload,
        })
        return
      }

      const groupedResources = groupBy(
        event.payload.entry?.map((entry) => entry.resource).filter((r) => !!r),
        'resourceType'
      )
      Object.entries(groupedResources).forEach(([resourceType, resources]) => {
        const schema = tryGetSchema(resourceType as ResourceType)
        if (!schema) {
          return
        }

        const queries = cache.findAll({
          queryKey: [resourceType],
        })
        queries.forEach((query) => {
          const {
            queryParams,
            resourceClass,
            resourceType: metaResourceType,
          } = query.meta ?? {}
          if (!queryParams) {
            return
          }
          if (!resourceClass && !metaResourceType) {
            return
          }
          const resourceType = metaResourceType ?? resourceClass?.RESOURCE_TYPE

          if (query.queryKey.includes(NO_QUERY)) {
            return
          }

          // `matchesSearchRequest` does not support the `_filter` or `_id` query parameters. The following is a fallback that ensures queries relying on those parameters maintain an up to date cache.
          // This is unoptimized and may lead to unnecessary query refetching. Once Medplum adds support for those parameters in `matchesSearchRequest`, we should remove this fallback.
          // https://github.com/medplum/medplum/issues/5397
          // https://github.com/medplum/medplum/issues/3627
          // https://seenhealth.slack.com/archives/C078NUJP6B1/p1742924256598569?thread_ts=1729017290.379799&cid=C078NUJP6B1
          if (
            '_filter' in queryParams ||
            '_id' in queryParams ||
            // chained search parameters aren't supported by `matchesSearchRequest`
            Object.keys(queryParams).some(
              (key) => key.includes('.') || key.startsWith('_has')
            ) ||
            resourceType === 'Appointment'
          ) {
            throttledFetch(query)
            return
          }

          const searchRequest = parseSearchRequest(
            fhirSearchUrl(resourceType, normalizeQuery(queryParams), medplum),
            schema
          )

          const currentData = queryClient.getQueryData(query.queryKey)

          // We cannot reliably determine if the query data is an array or a single resource, so we fallback to refetching the query.
          // We could derive from the query key but that would be more brittle.
          if (currentData === undefined) {
            throttledFetch(query)
            return
          }

          const newResources = resources.map((r) => {
            const extended = resourceClass
              ? resourceClass.fromFhirResource(r)
              : r
            return extended
          })

          const currentDataArray = compact(castArray(currentData))
          const updatedData = uniqBy(
            [...newResources, ...currentDataArray],
            'id'
          ).filter((r) => {
            const resource =
              r instanceof ExtendedResource ? r.toFhirResource() : r
            return matchesSearchRequest(resource, searchRequest, schema)
          })

          const currentDataVersions = currentDataArray.map((r) => {
            const resource =
              r instanceof ExtendedResource ? r.toFhirResource() : r
            return resource.meta?.versionId
          })
          const updatedDataVersions = updatedData.map((r) => {
            const resource =
              r instanceof ExtendedResource ? r.toFhirResource() : r
            return resource.meta?.versionId
          })
          // Prevent unnecessary query cache updates and subsequent re-renders if the current cache data is the same as the new data.
          // Given the wide net of criteria that is casted on the Subscription, this is expected to happen frequently and should be a good optimization.
          if (isEqual(currentDataVersions, updatedDataVersions)) {
            return
          }

          // The related resource's history queries need to be refetched to stay in sync with the latest resource versions.
          // There is an opportunity to optimize by locally updating the history queries cache data (i.e. pushing the new version to the cache), obviating the need for another network call.
          newResources.forEach((r) => {
            const historyQueries = cache.findAll({
              // TODO: fix the typing here
              queryKey: cacheKeys.readHistory(resourceType, r.id ?? ''),
            })
            historyQueries.forEach((query) => {
              throttledFetch(query)
            })
          })

          queryClient.setQueryData(
            query.queryKey,
            Array.isArray(currentData) ? updatedData : updatedData[0]
          )
        })
      })
    }

    subscriptionEmitter.addEventListener('message', messageHandler)
    subscriptionEmitter.addEventListener('error', errorHandler)
    subscriptionEmitter.addEventListener('connect', connectHandler)
    subscriptionEmitter.addEventListener('disconnect', disconnectHandler)

    return () => {
      subscriptionEmitter.removeEventListener('message', messageHandler)
      subscriptionEmitter.removeEventListener('error', errorHandler)
      subscriptionEmitter.removeEventListener('connect', connectHandler)
      subscriptionEmitter.removeEventListener('disconnect', disconnectHandler)
      throttledFetchesAtMount.forEach((fetch) => {
        fetch.cancel()
      })
      throttledFetchesAtMount.clear()
      latestQueriesAtMount.clear()
    }
  }, [queryClient, medplum, throttledFetch])
}
